import React, { useMemo } from 'react'; // <-- MEJORA: Importar useMemo
import { Bar } from 'react-chartjs-2'; // eslint-disable-line
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement, // Changed from PointElement and LineElement
  PointElement, // Still needed for the line chart part
  LineElement,  // Still needed for the line chart part
  Title,
  Tooltip,
  Legend, // eslint-disable-line no-unused-vars
  Filler,
} from 'chart.js';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler
);

const FinancialChart = ({ transactions = [], range = '6m' }) => { // <-- MEJORA: Fallback para robustez

  // --- MEJORA: Usar useMemo para optimizar el procesamiento de datos ---
  const chartData = useMemo(() => {
    if (!Array.isArray(transactions) || transactions.length === 0) {
      return { labels: [], gains: [], withdrawals: [], cumulative: [] };
    }

    const monthlyMap = new Map();

    transactions.forEach((tx) => {
      const timestamp = tx?.createdAt;
      const date = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : (timestamp instanceof Date ? timestamp : null);
      if (!date) return;

      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, {
          gains: 0,
          withdrawals: 0,
          label: date.toLocaleString('es-ES', { month: 'short', year: '2-digit' }),
          dateRef: new Date(date.getFullYear(), date.getMonth(), 1)
        });
      }

      const bucket = monthlyMap.get(monthKey);
      const amount = Number(tx.amount) || 0;
      if (['ganancia_roi', 'commission', 'commission_bonus', 'commission_manual_fix', 'commission_level_bonus'].includes(tx.type)) {
        bucket.gains += amount;
      } else if (tx.type === 'retiro_aprobado') {
        bucket.withdrawals += Math.abs(amount);
      }
    });

    const orderedEntries = Array.from(monthlyMap.values()).sort((a, b) => a.dateRef - b.dateRef);
    if (!orderedEntries.length) {
      return { labels: [], gains: [], withdrawals: [], cumulative: [] };
    }

    const lastDate = orderedEntries[orderedEntries.length - 1].dateRef;
    let filteredEntries = orderedEntries;
    const now = new Date();

    const getStartDateFromMonths = (months) => {
      if (!lastDate) return null;
      const start = new Date(lastDate);
      start.setMonth(start.getMonth() - (months - 1));
      start.setDate(1);
      return start;
    };

    if (range === '6m') {
      const start = getStartDateFromMonths(6);
      filteredEntries = orderedEntries.filter(entry => entry.dateRef >= start);
    } else if (range === '12m') {
      const start = getStartDateFromMonths(12);
      filteredEntries = orderedEntries.filter(entry => entry.dateRef >= start);
    } else if (range === 'ytd') {
      filteredEntries = orderedEntries.filter(entry => entry.dateRef.getFullYear() === now.getFullYear());
    }

    let runningNet = 0;
    const labels = filteredEntries.map(entry => entry.label);
    const gains = filteredEntries.map(entry => Number(entry.gains.toFixed(2)));
    const withdrawals = filteredEntries.map(entry => -Number(entry.withdrawals.toFixed(2))); // Negativo para visualización
    const cumulative = filteredEntries.map((entry, index) => {
      const monthlyNet = gains[index] + withdrawals[index];
      runningNet += monthlyNet;
      return Number(runningNet.toFixed(2));
    });

    return { labels, gains, withdrawals, cumulative };
  }, [transactions, range]); // El cálculo solo se re-ejecuta si 'transactions' o el rango cambian

  if (chartData.labels.length === 0) {
    return (
      <div className="chart-container chart-empty-state">
        <p>No hay movimientos suficientes para graficar el periodo seleccionado.</p>
      </div>
    );
  }

  const data = {
    labels: chartData.labels,
    datasets: [
      {
        type: 'bar', // Especificar tipo de gráfico
        label: 'Resultados',
        data: chartData.gains,
        backgroundColor: 'rgba(22, 163, 74, 0.65)', // Color más sólido para barras
        borderColor: 'rgb(22, 163, 74)',
        borderWidth: 1,
        stack: 'cashflow',
        borderRadius: 6,
      },
      {
        type: 'bar', // Especificar tipo de gráfico
        label: 'Retiros',
        data: chartData.withdrawals,
        backgroundColor: 'rgba(220, 38, 38, 0.65)', // Color más sólido para barras
        borderColor: 'rgb(220, 38, 38)',
        borderWidth: 1,
        stack: 'cashflow',
        borderRadius: 6,
      },
      {
        type: 'line', // Especificar tipo de gráfico
        label: 'Saldo acumulado',
        data: chartData.cumulative,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        fill: true,
        tension: 0.35,
        pointRadius: 4, // Hacer los puntos más visibles
        pointBackgroundColor: '#fff',
        pointBorderColor: 'rgb(59, 130, 246)',
        yAxisID: 'y',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    layout: {
      padding: 8,
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          callback: function(value) {
            return new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
          }
        },
        grid: {
          color: 'rgba(15, 23, 42, 0.08)'
        }
      },
      x: {
        stacked: true,
        grid: {
          display: false, // Ocultar la grilla vertical para un look más limpio
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              const value = context.dataset.label === 'Retiros'
                ? Math.abs(context.parsed.y)
                : context.parsed.y;
              label += new Intl.NumberFormat('es-VE', { style: 'currency', currency: 'USD' }).format(value);
            }
            return label;
          }
        }
      },
      legend: {
        position: 'top', // Posicionar la leyenda arriba
        labels: {
          usePointStyle: true,
        }
      }
    }
  };

  return <div className="chart-container"><Bar options={options} data={data} /></div>;
};

export default FinancialChart;